import { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { uploadViaPresignedUrl, uploadViaChunks } from '../workout-editor/videoUploadHelpers';
import { captureVideoFrame } from '../../utils/videoFrame';
import {
  Loader2, Plus, Search, Edit, Trash2, Dumbbell, Sparkles, Save, X,
  Youtube, Image as ImageIcon, Upload,
} from 'lucide-react';

// Allowed muscle groups (kept in sync with the seed list — admins can still
// type whatever they want; this is just for the chip picker).
const COMMON_MUSCLES = [
  'chest', 'back', 'shoulders', 'biceps', 'triceps', 'core', 'obliques',
  'quads', 'hamstrings', 'glutes', 'calves', 'forearms', 'cardio', 'full body',
];

const EQUIPMENT_OPTIONS = ['bodyweight', 'barbell', 'dumbbell', 'kettlebell', 'cable', 'machine', 'bands'];
const DIFFICULTY_OPTIONS = ['beginner', 'intermediate', 'advanced'];

/**
 * Admin tab for managing the Exercise Library (the canonical "movelist").
 * Lets admins:
 *   - Browse + search the seeded library
 *   - Create, edit, delete exercises (videos via YouTube URL or S3 upload)
 *   - Run a one-click "auto-link" that fuzzy-matches free-text exercises in
 *     existing workouts against the library and patches `exercise_id`.
 */
export const AdminExerciseLibraryTab = ({ api }) => {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null);  // {} for new, {...} for edit
  const [autoLinking, setAutoLinking] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await api().get('/exercises');
      setExercises(res.data.exercises || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load exercises');
    }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!query.trim()) return exercises;
    const q = query.toLowerCase();
    return exercises.filter(e =>
      e.name.toLowerCase().includes(q) ||
      e.slug.toLowerCase().includes(q) ||
      (e.aliases || []).some(a => a.toLowerCase().includes(q)) ||
      (e.muscle_groups || []).some(m => m.toLowerCase().includes(q))
    );
  }, [exercises, query]);

  const handleDelete = async (ex) => {
    if (!window.confirm(`Delete "${ex.name}"? Workouts linked to it will keep the name but lose the demo video / muscle tags.`)) return;
    try {
      await api().delete(`/admin/exercises/${ex.id}`);
      toast.success('Exercise deleted');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const handleAutoLink = async () => {
    if (!window.confirm('Auto-link all workouts to library exercises by fuzzy-matching exercise names? This only fills empty links — nothing already linked will be changed.')) return;
    setAutoLinking(true);
    try {
      const res = await api().post('/admin/exercises/auto-link');
      const d = res.data;
      toast.success(`Linked ${d.linked} exercises across ${d.scanned_workouts} workouts (${d.no_match} unmatched)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Auto-link failed');
    }
    setAutoLinking(false);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div data-testid="admin-exercise-library-tab">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, alias, or muscle group…"
            className="pl-10 bg-card border-border text-white"
            data-testid="exercise-library-search"
          />
        </div>
        <Button
          onClick={handleAutoLink}
          disabled={autoLinking}
          variant="outline"
          className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
          data-testid="exercise-library-autolink-btn"
        >
          {autoLinking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
          Auto-Link Workouts
        </Button>
        <Button
          onClick={() => setEditing({})}
          className="bg-primary text-black hover:bg-primary/90"
          data-testid="exercise-library-create-btn"
        >
          <Plus className="h-4 w-4 mr-2" /> New Exercise
        </Button>
      </div>

      {/* Summary chip */}
      <div className="text-muted-foreground text-sm mb-4">
        Showing <span className="text-white font-medium">{filtered.length}</span> of {exercises.length} exercises
      </div>

      {/* Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((ex) => (
          <div
            key={ex.id}
            className="bg-card border border-border p-4 flex flex-col gap-2"
            data-testid={`exercise-card-${ex.slug}`}
          >
            <div className="flex items-start gap-3">
              {(ex.thumbnail_url || ex.image_url) ? (
                <img src={ex.thumbnail_url || ex.image_url} alt="" className="w-16 h-16 object-cover rounded" />
              ) : (
                <div className="w-16 h-16 bg-black/40 border border-dashed border-border rounded flex items-center justify-center text-muted-foreground">
                  <Dumbbell className="h-5 w-5" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h4 className="font-heading text-base text-white uppercase tracking-wider truncate">{ex.name}</h4>
                <p className="text-xs text-muted-foreground truncate">{ex.slug}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {(ex.muscle_groups || []).slice(0, 3).map(m => (
                    <Badge key={m} className="text-[10px] bg-primary/20 text-primary border-0">{m}</Badge>
                  ))}
                  {ex.equipment && (
                    <Badge className="text-[10px] bg-blue-500/20 text-blue-300 border-0">{ex.equipment}</Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className={`text-[10px] uppercase tracking-wider ${(ex.video_url || ex.youtube_url) ? 'text-green-400' : 'text-muted-foreground'}`}>
                {ex.video_url ? 'Video ✓' : ex.youtube_url ? 'YouTube ✓' : 'No video'}
              </span>
              <span className="ml-auto flex gap-1">
                <button
                  onClick={() => setEditing(ex)}
                  className="p-1.5 rounded text-muted-foreground hover:text-primary hover:bg-primary/10"
                  aria-label="Edit"
                  data-testid={`exercise-edit-${ex.slug}`}
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(ex)}
                  className="p-1.5 rounded text-muted-foreground hover:text-red-400 hover:bg-red-500/10"
                  aria-label="Delete"
                  data-testid={`exercise-delete-${ex.slug}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </span>
            </div>
          </div>
        ))}
        {!filtered.length && (
          <div className="col-span-full text-center text-muted-foreground py-12">
            No exercises match your search.
          </div>
        )}
      </div>

      {editing && (
        <ExerciseEditorModal
          api={api}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); fetchAll(); }}
        />
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Modal — create/edit exercise
// ---------------------------------------------------------------------------
const ExerciseEditorModal = ({ api, initial, onClose, onSaved }) => {
  const { token } = useAuth();
  const isNew = !initial.id;
  const abortRef = useRef(null);
  const [form, setForm] = useState({
    slug: initial.slug || '',
    name: initial.name || '',
    description: initial.description || '',
    aliases: (initial.aliases || []).join(', '),
    muscle_groups: initial.muscle_groups || [],
    equipment: initial.equipment || 'bodyweight',
    difficulty: initial.difficulty || 'beginner',
    form_tips: (initial.form_tips || []).join('\n'),
    video_url: initial.video_url || '',
    youtube_url: initial.youtube_url || '',
    thumbnail_url: initial.thumbnail_url || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);

  const toggleMuscle = (m) => {
    setForm(f => ({
      ...f,
      muscle_groups: f.muscle_groups.includes(m)
        ? f.muscle_groups.filter(x => x !== m)
        : [...f.muscle_groups, m],
    }));
  };

  const handleThumbUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingThumb(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api().post('/admin/images/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, thumbnail_url: res.data.public_url }));
      toast.success('Thumbnail uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    }
    setUploadingThumb(false);
    e.target.value = '';
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingVideo(true);
    setVideoProgress(0);
    const apiBase = process.env.REACT_APP_BACKEND_URL;
    try {
      let result;
      try {
        // Preferred: presigned PUT directly to S3 — no size limit, fastest path
        result = await uploadViaPresignedUrl({
          apiBase, token, file,
          workoutId: null,
          onProgress: setVideoProgress,
          abortRef,
        });
      } catch (presignErr) {
        // Fallback to chunked base64 upload if S3 PUT is blocked by CORS / network
        console.warn('Presigned upload failed, falling back to chunked:', presignErr.message);
        toast.message('Switching to chunked upload (this may take longer)…');
        result = await uploadViaChunks({
          apiBase, token, file,
          workoutId: null,
          onProgress: setVideoProgress,
          abortRef,
        });
      }
      setForm(f => ({ ...f, video_url: result.video_url || result.url || result.public_url }));
      toast.success('Video uploaded');

      // Auto-generate a thumbnail from the video's first frame IF the admin
      // hasn't already set one. Keeps the exercise library card from falling
      // back to the generic dumbbell placeholder after upload.
      try {
        const needsThumb = !form.thumbnail_url;
        if (needsThumb) {
          const frameBlob = await captureVideoFrame(file, 0.5);
          const fd = new FormData();
          fd.append('file', new File([frameBlob], `${(form.slug || 'exercise')}-thumb.png`, { type: 'image/png' }));
          const thumbRes = await api().post('/admin/images/upload', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
          if (thumbRes.data?.public_url) {
            setForm(f => ({ ...f, thumbnail_url: thumbRes.data.public_url }));
            toast.success('Thumbnail generated from video');
          }
        }
      } catch (thumbErr) {
        console.warn('Auto-thumbnail failed:', thumbErr.message);
      }
    } catch (err) {
      toast.error(err.message || err.response?.data?.detail || 'Video upload failed');
    }
    setUploadingVideo(false);
    setVideoProgress(0);
    e.target.value = '';
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    const payload = {
      ...form,
      aliases: form.aliases.split(',').map(s => s.trim()).filter(Boolean),
      form_tips: form.form_tips.split('\n').map(s => s.trim()).filter(Boolean),
    };
    try {
      if (isNew) {
        await api().post('/admin/exercises', payload);
        toast.success('Exercise created');
      } else {
        await api().put(`/admin/exercises/${initial.id}`, payload);
        toast.success('Exercise updated');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 py-8 overflow-y-auto"
      onClick={() => !saving && onClose()}
      data-testid="exercise-editor-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-card border-2 border-primary/40 p-6 max-w-2xl w-full my-auto"
      >
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl text-white uppercase tracking-wider">
            {isNew ? 'New Exercise' : `Edit ${initial.name}`}
          </h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-white" data-testid="exercise-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Name *</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Bench Press"
                data-testid="exercise-form-name"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Slug</label>
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                placeholder="auto-generated from name"
                disabled={!isNew}
                data-testid="exercise-form-slug"
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Aliases (comma-separated)</label>
            <Input
              value={form.aliases}
              onChange={(e) => setForm({ ...form, aliases: e.target.value })}
              placeholder="BB Bench, Barbell Bench, Flat Bench"
              data-testid="exercise-form-aliases"
            />
            <p className="text-[10px] text-muted-foreground mt-1">
              Used by Auto-Link to match free-text exercise names in existing workouts.
            </p>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Muscle Groups</label>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="exercise-form-muscles">
              {COMMON_MUSCLES.map(m => (
                <button
                  key={m}
                  type="button"
                  onClick={() => toggleMuscle(m)}
                  className={`px-3 py-1 text-xs uppercase tracking-wider border transition-colors ${
                    form.muscle_groups.includes(m)
                      ? 'bg-primary text-black border-primary'
                      : 'bg-card text-muted-foreground border-border hover:border-primary/40'
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Equipment</label>
              <select
                value={form.equipment}
                onChange={(e) => setForm({ ...form, equipment: e.target.value })}
                className="w-full bg-card border border-border text-white px-3 py-2 mt-1"
                data-testid="exercise-form-equipment"
              >
                {EQUIPMENT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Difficulty</label>
              <select
                value={form.difficulty}
                onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
                className="w-full bg-card border border-border text-white px-3 py-2 mt-1"
                data-testid="exercise-form-difficulty"
              >
                {DIFFICULTY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Form Tips (one per line)</label>
            <textarea
              value={form.form_tips}
              onChange={(e) => setForm({ ...form, form_tips: e.target.value })}
              rows={4}
              className="w-full bg-card border border-border text-white px-3 py-2 mt-1 text-sm"
              placeholder={`Brace your core\nDrive through your heels\nFull range of motion`}
              data-testid="exercise-form-tips"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Youtube className="h-3 w-3" /> YouTube URL
              </label>
              <Input
                value={form.youtube_url}
                onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=..."
                data-testid="exercise-form-youtube"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Or S3 Video URL</label>
              <Input
                value={form.video_url}
                onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                placeholder="https://...s3.amazonaws.com/videos/..."
                data-testid="exercise-form-video"
              />
              <div className="flex items-center gap-3 mt-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,video/x-m4v,video/*"
                    onChange={handleVideoUpload}
                    className="hidden"
                    data-testid="exercise-form-video-input"
                    disabled={uploadingVideo}
                  />
                  <span className="inline-flex items-center gap-2 px-3 py-2 border border-border text-white text-xs hover:bg-card/80 rounded">
                    {uploadingVideo ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                    {uploadingVideo ? `Uploading ${videoProgress}%` : (form.video_url ? 'Replace video' : 'Upload video')}
                  </span>
                </label>
                {form.video_url && !uploadingVideo && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, video_url: '' })}
                    className="text-xs text-muted-foreground hover:text-red-400"
                  >
                    Remove
                  </button>
                )}
                <span className="text-[10px] text-muted-foreground ml-auto">Any size · mp4 / webm / mov</span>
              </div>
              {form.video_url && !uploadingVideo && (
                <video
                  src={form.video_url}
                  controls
                  playsInline
                  className="mt-2 max-h-40 w-full rounded border border-border bg-black"
                  data-testid="exercise-form-video-preview"
                />
              )}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <ImageIcon className="h-3 w-3" /> Thumbnail
            </label>
            <div className="flex items-center gap-3 mt-1">
              {form.thumbnail_url ? (
                <img src={form.thumbnail_url} alt="" className="w-16 h-16 object-cover rounded border border-border" />
              ) : (
                <div className="w-16 h-16 bg-black/40 border border-dashed border-border rounded flex items-center justify-center text-muted-foreground">
                  <ImageIcon className="h-5 w-5" />
                </div>
              )}
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleThumbUpload}
                  className="hidden"
                  data-testid="exercise-form-thumb-input"
                />
                <span className="inline-flex items-center gap-2 px-3 py-2 border border-border text-white text-sm hover:bg-card/80">
                  {uploadingThumb ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                  {form.thumbnail_url ? 'Replace' : 'Upload'}
                </span>
              </label>
              {form.thumbnail_url && (
                <button
                  onClick={() => setForm({ ...form, thumbnail_url: '' })}
                  className="text-xs text-muted-foreground hover:text-red-400"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <Button onClick={onClose} variant="outline" className="flex-1" disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-primary text-black hover:bg-primary/90"
            data-testid="exercise-form-save"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            {isNew ? 'Create Exercise' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};
